/**
 * Where the pre-beta reader's URLs go now.
 *
 * Every entry here is a URL somebody may still hold: a bookmark, a pasted
 * Slack link, an old email. None of them render anything; they resolve to
 * the beta reader (`/`, `/?edit=1`, `/?search=`, `/feeds/:id`), the public
 * story permalink, or the admin article browser. Old search ids are the one
 * case that needs the server (the search page at `/search/:id` reads what an
 * id names), so the shapes below that carry a search id all funnel into that
 * route.
 *
 * The v2 reader's preview paths (`/v2`, `/v2/feeds`, `/v2/search`,
 * `/feeds/v2/:id`) became the reader's own paths at the cutover and resolve
 * to them, query preserved.
 *
 * Retired without a redirect: `/flagged` (the bookmark feature is gone),
 * `/reader/smartfeed/:ruleId` (rule channels were never shareable) and
 * `/search?<filters>` (the old admin article search, now `/admin/search`;
 * `/search` is the reader's search page since the v2 cutover).
 */

/** Paths that map to one fixed destination. Matched exactly. */
export const LEGACY_STATIC_REDIRECTS: Readonly<Record<string, string>> = {
  '/tldr': '/',
  '/for-you': '/',
  '/search/new': '/?edit=1',
  // Saved searches are managed on the Feeds page; public prompts are edited
  // under /admin/public-searches.
  '/saved-searches': '/feeds',
  '/searches': '/feeds',
  // Search models are part of the search prompts' versions, in Prompt Lab.
  '/admin/search-prompt': '/admin/prompt-lab/one_pass_search',
};

/** Route patterns whose target depends on the URL; resolved by `legacyRedirectTarget`. */
export const LEGACY_DYNAMIC_ROUTES: readonly string[] = [
  '/fastsearch/*',
  '/search/saved/*',
  '/search/runs/*',
  '/search/descriptions/*',
  '/search/:searchId/audit',
  '/reader',
  '/read/:articleId',
  '/admin/article/:articleId',
  '/v2',
  '/v2/feeds',
  '/v2/search',
  '/feeds/v2/:feedId',
];

const V2_PREVIEW_PATHS: Readonly<Record<string, string>> = { '/v2': '/', '/v2/feeds': '/feeds', '/v2/search': '/search' };

// The per-kind segments the old search URLs interposed between the prefix and
// the id. They carried no information the page cannot recover from the id.
const LEGACY_SEARCH_KIND_SEGMENTS = new Set(['saved', 'runs', 'descriptions']);

/**
 * The beta destination for a legacy `pathname` + `search` (query string, with
 * its leading `?`), or null when the path is not a legacy shape.
 *
 * - `/fastsearch/<kind>/<id>[/audit]`, `/search/<kind>/<id>[/audit]`, `/search/<id>/audit` ->
 *   `/search/<id>`, query preserved; with no id, the composer. The fast-search
 *   audit is retired with fast search, so an audit link reopens its search.
 * - `/reader?channel=search:<id>` (or `smart:<id>`) -> `/search/<id>`;
 *   `/reader?compose=new` -> the interest editor; any other channel -> `/`.
 * - `/read/<article id>` -> `/story/<article id>`.
 * - `/admin/article/<id>` (the retired admin edit page) -> `/article/admin/<id>`,
 *   query preserved, so `/admin/article/new?url=…` lands on the create form.
 * - `/v2`, `/v2/feeds`, `/v2/search`, `/feeds/v2/<id>` -> `/`, `/feeds`,
 *   `/search`, `/feeds/<id>`, query preserved.
 */
export function legacyRedirectTarget(pathname: string, search = ''): string | null {
  const fixed = LEGACY_STATIC_REDIRECTS[pathname];
  if (fixed) return fixed;

  const segments = pathname.split('/').filter(Boolean);
  const [head, ...rest] = segments;

  if (head === 'fastsearch' || (head === 'search' && rest.length > 0 && LEGACY_SEARCH_KIND_SEGMENTS.has(rest[0]))) {
    const tail = [...rest];
    if (tail.length && LEGACY_SEARCH_KIND_SEGMENTS.has(tail[0])) tail.shift();
    if (!tail.length || tail[0] === 'new') return '/?edit=1';
    if (tail[tail.length - 1] === 'audit') tail.pop();
    return `/search/${tail.join('/')}${search}`;
  }

  if (head === 'search' && rest.length === 2 && rest[1] === 'audit') return `/search/${encodeURIComponent(rest[0])}${search}`;

  if (pathname === '/reader') {
    const params = new URLSearchParams(search);
    if (params.get('compose') === 'new') return '/?edit=1';
    const searchId = (params.get('channel') || '').match(/^(?:search|smart):(.+)$/)?.[1];
    return searchId ? `/search/${encodeURIComponent(searchId)}` : '/';
  }

  if (head === 'read' && rest.length === 1) return `/story/${encodeURIComponent(rest[0])}`;

  if (head === 'admin' && rest.length === 2 && rest[0] === 'article') {
    return `/article/admin/${encodeURIComponent(rest[1])}${search}`;
  }

  // A trailing slash (`/v2/feeds/`, `/v2/search/?q=`) names the same page.
  const v2Preview = V2_PREVIEW_PATHS[pathname.replace(/\/+$/, '') || '/'];
  if (v2Preview) return `${v2Preview}${search}`;
  if (head === 'feeds' && rest.length === 2 && rest[0] === 'v2') return `/feeds/${encodeURIComponent(rest[1])}${search}`;

  return null;
}
